import React from 'react';
import { useNavigate } from 'react-router-dom';
import RoutePaths from '../routes/RoutePaths';
import {
    Box,
    Button,
    Checkbox,
    IconButton,
    Link,
    ListItemText,
    MenuItem,
    Popover,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';


import { useIntl } from 'react-intl';
import {
    FILTER_HEADER_HEIGHT_BASE,
    NEWS_QUEUE_TITLE,
    PUBLISH_STATE_OPTIONS,
} from './NewsQueueConstants';

function FilterSelect({ label, value, onChange, options, emptyLabel, isGrouped = false, availableSet }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const labelByValue = React.useMemo(
        () => Object.fromEntries(options.map((opt) => [opt.value, opt.label])),
        [options]
    );

    const displayText = 
        !value || value.length === 0
            ? emptyLabel
            : (() => {
                const selectedLabels = value.map((v) => {
                    // Capitalize first letter for display
                    const label = labelByValue[v] ?? v;
                    return typeof label === 'string' 
                        ? label.charAt(0).toUpperCase() + label.slice(1)
                        : label;
                });
                
                // Show first 3 items, then ellipsis if more
                if (selectedLabels.length > 3) {
                    return `${selectedLabels.slice(0, 3).join(', ')}, ...`;
                }
                return selectedLabels.join(', ');
            })();

    return (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: 1, minWidth: 120 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 300, whiteSpace: 'nowrap' }}>
                {label}
            </Typography>
            <Button
                size="small"
                onClick={(event) => setAnchorEl(event.currentTarget)}
                sx={{ textTransform: 'none', borderColor: 'divider', fontWeight: 400 }}
            >
                {displayText}
            </Button>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: 1,
                        minWidth: 220,
                        maxHeight: 280,
                        overflowY: 'auto',
                    },
                }}
            >
                {options.map((opt) => {
                    const isMainCategory = opt.isMainCategory;
                    const handleClick = () => {
                        if (isMainCategory && opt.subCategories) {
                            // Toggle select all/none for subcategories
                            const allSubsSelected = opt.subCategories.every(sub => value.includes(sub));
                            const next = allSubsSelected
                                ? value.filter(v => !opt.subCategories.includes(v))
                                : [...value, ...opt.subCategories.filter(sub => !value.includes(sub))];
                            onChange?.(next);
                        } else {
                            // Regular category selection
                            const next = value.includes(opt.value)
                                ? value.filter((v) => v !== opt.value)
                                : [...value, opt.value];
                            onChange?.(next);
                        }
                    };

                    const isChecked = isMainCategory && opt.subCategories
                        ? opt.subCategories.every(sub => value.includes(sub))
                        : value.includes(opt.value);

                    const isIndeterminate = isMainCategory && opt.subCategories
                        ? opt.subCategories.some(sub => value.includes(sub)) && !opt.subCategories.every(sub => value.includes(sub))
                        : false;

                    return (
                        <React.Fragment key={opt.value}>
                            <MenuItem
                                onClick={handleClick}
                                disabled={!opt.isAvailable}
                                sx={
                                    opt.isAvailable === false
                                        ? { opacity: 0.5 }
                                        : undefined
                                }
                            >
                                <Checkbox 
                                    checked={isChecked}
                                    indeterminate={isIndeterminate}
                                    size="small" 
                                />
                                <ListItemText
                                    primary={opt.label}
                                    primaryTypographyProps={{
                                        color: opt.isAvailable === false ? 'text.secondary' : undefined,
                                        sx: isMainCategory ? { fontWeight: 'bold' } : { pl: 2 }
                                    }}
                                />
                            </MenuItem>
                            {isMainCategory && opt.subCategories && opt.subCategories.map(sub => {
                                // Capitalize first letter for subcategory display
                                const subLabel = sub.charAt(0).toUpperCase() + sub.slice(1);
                                const isSubAvailable = availableSet ? availableSet.has(sub) : true;
                                return (
                                    <MenuItem
                                        key={sub}
                                        onClick={() => {
                                            const next = value.includes(sub)
                                                ? value.filter((v) => v !== sub)
                                                : [...value, sub];
                                            onChange?.(next);
                                        }}
                                        disabled={!isSubAvailable}
                                        sx={{ pl: 4 }}
                                    >
                                        <Checkbox checked={value.includes(sub)} size="small" />
                                        <ListItemText
                                            primary={subLabel}
                                            primaryTypographyProps={{
                                                color: !isSubAvailable ? 'text.secondary' : undefined
                                            }}
                                        />
                                    </MenuItem>
                                );
                            })}
                        </React.Fragment>
                    );
                })}
            </Popover>
        </Stack>
    );
}

export default function NewsQueueFilter({
    filterOpen,
    onToggleFilterOpen,

    sortDirection = 'desc',
    onToggleSortDirection,

    categories = [],
    categoriesByGroup = {},
    availableCategories = [],
    selectedCategories = [],
    onSelectedCategoriesChange,

    sources = [],
    availableSources = [],
    selectedSources = [],
    onSelectedSourcesChange,

    targetMedias = [],
    availableTargetMedias = [],
    selectedTargetMedias = [],
    onSelectedTargetMediasChange,

    selectedStatuses = [],
    availableStatuses = [],
    onSelectedStatusesChange,
    configurations,
}) {
    const intl = useIntl();
    const navigate = useNavigate();

    const mediaLabel = intl.formatMessage({ id: 'newsQueue.filter.media' });
    const categoryLabel = intl.formatMessage({ id: 'newsQueue.filter.category' });
    const sourceLabel = intl.formatMessage({ id: 'newsQueue.filter.source' });
    const statusLabel = intl.formatMessage({ id: 'newsQueue.filter.status' });
    const allLabel = intl.formatMessage({ id: 'newsQueue.filter.all' });
    const filterButtonLabel = intl.formatMessage({ id: 'newsQueue.filter.toggle' });
    const filterTooltipLabel = intl.formatMessage({ id: 'newsQueue.filter.tooltip' });
    const selectMediaLabel = intl.formatMessage({ id: 'newsQueue.mediaSelection.select' });
    const sortDirectionAriaLabel = intl.formatMessage({
        id: 'newsQueue.sort.toggleDirection',
    });
    const newestFirstLabel = intl.formatMessage({ id: 'newsQueue.sort.newestFirst' });
    const oldestFirstLabel = intl.formatMessage({ id: 'newsQueue.sort.oldestFirst' });



    const safeSources = React.useMemo(
        () => Array.from(new Set(sources.filter(Boolean))),
        [sources]
    );

    const safeTargetMedias = React.useMemo(
        () => Array.from(new Set(targetMedias.filter(Boolean))),
        [targetMedias]
    );

    const availableTargetMediaSet = React.useMemo(
        () => new Set(availableTargetMedias.filter(Boolean)),
        [availableTargetMedias]
    );

    const availableCategorySet = React.useMemo(
        () => new Set(availableCategories.filter(Boolean)),
        [availableCategories]
    );

    const availableSourceSet = React.useMemo(
        () => new Set(availableSources.filter(Boolean)),
        [availableSources]
    );

    const availableStatusSet = React.useMemo(
        () => new Set(availableStatuses.filter(Boolean)),
        [availableStatuses]
    );



    const sourceLabelByValue = React.useMemo(() => {
        return Object.fromEntries(
            safeSources.map((s) => {
                const key = String(s).trim();
                // Capitalize first letter for display
                const displayName = key.charAt(0).toUpperCase() + key.slice(1);
                return [s, displayName];
            })
        );
    }, [safeSources]);

    const targetMediaLabelByValue = React.useMemo(() => {
        return Object.fromEntries(
            safeTargetMedias.map((m) => {
                const key = String(m).trim();
                // Use configured display name or fallback to capitalized key
                const displayName = configurations?.media_name_by_target_media?.[key] ?? (key.charAt(0).toUpperCase() + key.slice(1));
                return [m, displayName];
            })
        );
    }, [safeTargetMedias, configurations?.media_name_by_target_media]);

    return (
        <Box
            sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: 'background.paper',
                borderBottom: '1px solid',
                borderColor: 'divider',
                //borderBottom: '3px solid #0e57b6',
                px: 0,
                ml: "-10px",
                py: 1,
                minHeight: FILTER_HEADER_HEIGHT_BASE,
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                justifyContent: 'flex-start',
            }}
        >
            <Stack direction="row" alignItems="center" justifyContent="center" sx={{ width: '100%' }}>
                <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: 'primary.main', fontFamily: 'monospace' }}
                >
                    {NEWS_QUEUE_TITLE}
                </Typography>
                <Tooltip title={filterTooltipLabel} arrow>
                    <IconButton aria-label={filterButtonLabel} onClick={onToggleFilterOpen} color="primary">
                        <FilterListIcon />
                    </IconButton>
                </Tooltip>
            </Stack>

            <Stack direction="row" alignItems="center" justifyContent="center" 
            sx={{ mt: 1, width: '100%', px: 2 }}>
                <Typography
                    variant="body2"
                    sx={{ 
                        fontWeight: 'bold',
                        color: 'primary.main',
                        fontSize: '0.875rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 'calc(100% - 40px)'
                    }}
                >
                    {selectedTargetMedias && selectedTargetMedias.length > 0 
                        ? selectedTargetMedias.map(media => targetMediaLabelByValue[media] ?? media).join(', ')
                        : safeTargetMedias.map(media => targetMediaLabelByValue[media] ?? media).join(', ')}
                </Typography>
            </Stack>

            {filterOpen && (
                <Stack
                    direction="row"
                    sx={{
                        mt: 2,
                        pl: "16px",
                        width: 'calc(100% - 16px)',
                        flexWrap: 'wrap',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        rowGap: 1,
                        columnGap: 2,
                    }}
                >
                    <Stack
                        direction="row"
                        sx={{
                            flex: '1',
                            minWidth: 0,
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            alignItems: 'center',
                            rowGap: 1,
                            columnGap: 1.5,
                        }}
                    >
                        { false && (
                        <FilterSelect
                            label={mediaLabel + ":"}
                            value={selectedTargetMedias}
                            onChange={onSelectedTargetMediasChange}
                            emptyLabel={allLabel}
                            options={safeTargetMedias.map((m) => ({
                                value: m,
                                label: targetMediaLabelByValue[m] ?? m,
                                isAvailable: availableTargetMediaSet.has(m),
                            }))}
                        />
                        ) }
                        <Link
                            component="button"
                            onClick={() => navigate(RoutePaths.newsQueue.mediaSelection)}
                            color="primary"
                            underline="hover"
                            sx={{ 
                                mr: 3, 
                                mb: 1, 
                                mt: 0.8, 
                                fontSize: '0.875rem', 
                                cursor: 'pointer' }}
                        >
                            {selectMediaLabel}
                        </Link>

                        <FilterSelect
                            label={categoryLabel + ":"}
                            value={selectedCategories}
                            onChange={onSelectedCategoriesChange}
                            emptyLabel={allLabel}
                            availableSet={availableCategorySet}
                            options={Object.entries(categoriesByGroup).map(([group, subCategories]) => ({
                                value: group,
                                label: group.charAt(0).toUpperCase() + group.slice(1),
                                isMainCategory: true,
                                subCategories: subCategories,
                                isAvailable: true, // Main categories are always available
                            }))}
                        />

                        {
                            false && (
                                <FilterSelect
                                    label={sourceLabel + ":"}
                                    value={selectedSources}
                                    onChange={onSelectedSourcesChange}
                                    emptyLabel={allLabel}
                                    options={safeSources.map((s) => ({
                                        value: s,
                                        label: sourceLabelByValue[s] ?? s,
                                        isAvailable: availableSourceSet.has(s),
                                    }))}
                                />
                            )
                        }

                        <FilterSelect
                            label={statusLabel + ":"}
                            value={selectedStatuses}
                            onChange={onSelectedStatusesChange}
                            emptyLabel={allLabel}
                            options={PUBLISH_STATE_OPTIONS.map((opt) => ({
                                ...opt,
                                isAvailable: availableStatusSet.has(opt.value),
                            }))}
                        />
                    </Stack>

                    <Stack
                        direction="row"
                        alignItems="center"
                        alignSelf="flex-end"
                        sx={{
                            flex: '0 0 auto',
                            justifyContent: 'flex-end',
                            ml: 'auto',
                            mb: 0.5,
                        }}
                    >
                        <IconButton
                            size="small"
                            aria-label={sortDirectionAriaLabel}
                            title={sortDirection === 'desc' ? newestFirstLabel : oldestFirstLabel}
                            onClick={onToggleSortDirection}
                            disabled={!onToggleSortDirection}
                        >
                            {sortDirection === 'desc' ? <ArrowDownwardIcon fontSize="small" /> : <ArrowUpwardIcon fontSize="small" />}
                        </IconButton>
                    </Stack>
                </Stack>
            )}
        </Box>
    );
}
